/**
 * /auth consent-page helpers — slice 3.
 *
 * The Vue component (``Auth.vue``) is the deliverable; this module is
 * its testable kernel:
 *
 * - {@link extractUserCode} pulls the user_code off the verification URL
 *   query string and validates its shape (8-char Crockford base32 in
 *   ABCD-1234 form: digits 0-9 plus A-Z excluding the confusable
 *   I/L/O/U).
 * - {@link submitConsent} POSTs the captured Firebase tokens to the
 *   API's ``/api/v2/auth/device/complete`` endpoint.
 *
 * Both functions take their dependencies as arguments so tests run
 * without touching real Firebase or the network.
 */

import { PLUGIN_KIND, PluginKind } from '@run-diffusion/shared';

/**
 * Crockford-base32 charset for connection codes — digits 0-9 plus the
 * letters A-Z, excluding the visually confusable I, L, O, U. Single
 * source of truth: the URL parser, the manual-entry mask, and the final
 * validator all derive their regexes from this so they can't drift
 * apart. Mirrors ``USER_CODE_CHARSET`` in ``api/auth/device_codes.py``
 * on the backend EXACTLY — a code the frontend accepts must be one the
 * backend can mint, so if either side changes the charset the other
 * must follow.
 */
const USER_CODE_CHARS = '0-9A-HJKMNP-TV-Z';

/**
 * Canonical User Code shape: 8 charset characters split ``XXXX-XXXX``.
 */
const USER_CODE_PATTERN = new RegExp(`^[${USER_CODE_CHARS}]{4}-[${USER_CODE_CHARS}]{4}$`);

/** Matches a single character NOT in the code charset (for stripping). */
const USER_CODE_STRIP = new RegExp(`[^${USER_CODE_CHARS}]`, 'g');


export function extractUserCode (queryString: string): string | null {
	if (!queryString) {
		return null;
	}
	// Tolerate leading "?" or full URLs — caller pastes whatever they have.
	const trimmed = queryString.startsWith('?') ? queryString.slice(1) : queryString;
	const params = new URLSearchParams(trimmed);
	const raw = params.get('code');
	if (!raw) {
		return null;
	}
	const candidate = raw.toUpperCase();
	return USER_CODE_PATTERN.test(candidate) ? candidate : null;
}


/**
 * Pull the plugin identifier off the verification URL query
 * (``?plugin=miro``), validated against the shared ``PLUGIN_KIND`` enum —
 * the ``plugin_kind`` each plugin sends to ``/auth/device/start``, echoed
 * back on the verification URL (see DeviceStartView in
 * api/auth/device_flow.py). Returns null when it's absent or not a known
 * plugin, so callers fall back to the generic "connected" screen.
 */
export function extractPluginId (queryString: string): PluginKind | null {
	if (!queryString) {
		return null;
	}
	const trimmed = queryString.startsWith('?') ? queryString.slice(1) : queryString;
	const raw = (new URLSearchParams(trimmed).get('plugin') || '').toLowerCase();
	const kinds: string[] = Object.values(PLUGIN_KIND);
	return kinds.includes(raw) ? (raw as PluginKind) : null;
}


/**
 * Detect a plugin sign-*up* intent on the verification URL (``?intent=signup``).
 * The rd-photoshop Gate's "Create a free account" button appends it (see
 * verificationUrlForIntent in the plugin's deviceFlow.ts); the "Sign in" button
 * omits it. When true and the visitor isn't authenticated, Auth.vue routes them
 * to the Login component's sign-up state (``?signup=true``) instead of the login
 * form, so a brand-new user creates an account rather than hitting a login wall.
 * Anything other than the exact ``signup`` token is treated as no intent, so the
 * default (login) is preserved. See ADR-0026.
 */
export function isSignUpIntent (queryString: string): boolean {
	if (!queryString) {
		return false;
	}
	const trimmed = queryString.startsWith('?') ? queryString.slice(1) : queryString;
	return (new URLSearchParams(trimmed).get('intent') || '').toLowerCase() === 'signup';
}


/**
 * Normalize a manually-typed connection code into canonical
 * ``XXXX-XXXX`` form, or return null if it can't be made valid.
 *
 * Forgiving on input so the user can type what they see on the
 * plugin without fighting the field:
 *   - lower/upper case → upper
 *   - surrounding / internal whitespace stripped
 *   - the dash is optional ("A7DC3Q9K" → "A7DC-3Q9K")
 *
 * Charset still excludes the confusable I/L/O/U (Crockford base32),
 * matching ``extractUserCode`` and the backend ``device_codes.py``.
 */
export function normalizeUserCode (raw: string): string | null {
	if (!raw) {
		return null;
	}
	let c = raw.toUpperCase().replace(/\s+/g, '');
	// Allow entry without the dash — insert it for a bare 8-char code.
	if (new RegExp(`^[${USER_CODE_CHARS}]{8}$`).test(c)) {
		c = `${c.slice(0, 4)}-${c.slice(4)}`;
	}
	return USER_CODE_PATTERN.test(c) ? c : null;
}


/**
 * Format a manually-typed connection code *as the user types it* — the
 * progressive, lenient counterpart to {@link normalizeUserCode}.
 *
 *   - uppercases
 *   - drops any character outside the code charset (incl. the user's
 *     own dashes, whitespace, punctuation)
 *   - caps at 8 charset characters (ignores overflow)
 *   - inserts the dash once 5+ characters are present, so the field
 *     reads ``XXXX-XXXX`` like the prefilled consent display
 *
 * Always returns a (possibly partial) display string — never null —
 * because it runs on every keystroke. Final validation is
 * {@link normalizeUserCode} / {@link USER_CODE_PATTERN}.
 */
export function maskUserCodeInput (raw: string): string {
	if (!raw) {
		return '';
	}
	const chars = raw.toUpperCase().replace(USER_CODE_STRIP, '').slice(0, 8);
	return chars.length > 4 ? `${chars.slice(0, 4)}-${chars.slice(4)}` : chars;
}


export interface SubmitConsentInput {
	/** API base URL, e.g. ``https://api2.rundiffusion.com/api/v2``. */
	baseUrl: string;
	userCode: string;
	idToken: string;
	refreshToken: string;
	pluginKind?: string;
}

export type SubmitConsentResult =
	| { ok: true }
	| {
		ok: false;
		reason: 'code_expired_or_invalid' | 'network' | 'failed';
		message?: string;
	};


export async function submitConsent (
	input: SubmitConsentInput,
): Promise<SubmitConsentResult> {
	const url = `${input.baseUrl.replace(/\/$/, '')}/auth/device/complete`;
	const body: Record<string, string> = {
		user_code: input.userCode,
		refresh_token: input.refreshToken,
	};
	if (input.pluginKind) {
		body.plugin_kind = input.pluginKind;
	}

	let resp: Response;
	try {
		resp = await fetch(url, {
			method: 'POST',
			// /complete is account-agnostic: the plugin picks an account per
			// later request, and with no team_id this call acts personal, which
			// is exactly right for a consent endpoint.
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${input.idToken}`,
			},
			body: JSON.stringify(body),
		});
	} catch (_err) {
		return { ok: false, reason: 'network' };
	}

	if (resp.status === 204) {
		return { ok: true };
	}
	if (resp.status === 404) {
		return { ok: false, reason: 'code_expired_or_invalid' };
	}
	return { ok: false, reason: 'failed' };
}
